<template>
  <video-background>
    <div v-if="showText && !hasScanError" class="flex flex-col w-full text-center mt-24 px-10">
      <h1 class="screen-info" :class="{ 'font-bold': isStamp }">{{ info }}</h1>
      <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    </div>
    <scan-error
      v-if="hasScanError"
      :reasons="scanErrorReasons"
      @retry="retryMobileScan"
      @close="$events.goto('HomePage')"
    />
  </video-background>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ScanProcess from '@/modules/scan-process';
import getScanErrorReason from '@/modules/scan-quality-error';
import { wait } from '@/modules/utils';
import page from '@/mixins/page';
import stampBgm from '@/mixins/stamp-bgm';
import autoClearTimer from '@/mixins/auto-clear-timer';
import audioPlayer from '@/mixins/audio-player';
import i18n from '@/i18n';

import VideoBackground from '@/components/common/VideoBackground.vue';
import ScanError from '@/components/MobileScan2/ScanError.vue';

export default {
  name: 'Analyzing',
  mixins: [page, audioPlayer, stampBgm, autoClearTimer],
  props: {
    second: Boolean,
    foot: Boolean,
  },
  components: {
    VideoBackground,
    ScanError,
  },
  async mounted() {
    if (!ScanProcess.tid) {
      this.$events.goto('HomePage');
      return;
    }
    this.errorTimer = setTimeout(() => {
      this.leaveWithError('Something went wrong. Please try again later.');
    }, 200000);
    this.stopAllAudio();
    if (this.isStamp) {
      this.playAnalyzing();
      this.playAudio('Voice7');
    } else if (ScanProcess.isMobile()) {
      this.playAudio('ms-20-analyzing');
    } else {
      this.playAudio('15-Creating_3D_avatar');
    }
    setTimeout(() => {
      this.showText = true;
    }, 5000);
    try {
      await Promise.all([this.clearScanRecords(0), this.waitFinish()]);
    } catch (e) {
      this.leaveWithError(e);
    }
  },
  beforeDestroy() {
    this.stopAnalyzing();
    if (typeof this.errorTimer !== 'undefined') {
      clearTimeout(this.errorTimer);
    }
  },
  data: () => ({
    showText: false,
    errorMsg: '',
    stampBgmEnded: false,
    reInitialized: false,
    url: '',
    // mobile scan only -- booth/stamp never reach the code that sets this
    scanErrorReasons: [],
  }),
  methods: {
    ...mapActions('user', ['clearScanRecords']),
    ...mapActions('app', ['clearStoreConfigs']),
    leaveWithError(error) {
      const msg = typeof error === 'string' ? error : error.message;
      this.errorMsg = msg;
      console.log(`[ERROR] Wait finished failed: ${msg}`, error);
      this.$events.alert(msg);
      this.$events.goto(this.isContactless ? 'Welcome' : 'HomePage', {
        params: { failed: true },
      });
    },
    showScanError(reasons, errno) {
      // the errno is deliberately not shown to the user -- the card names the actual problems, so
      // a support code would only add noise. Keep it in the log for diagnostics.
      console.log(`[SCAN] scan quality rejected: ${errno} -> ${reasons.join('+')}`);
      // the 200s watchdog would yank the user to HomePage while they are still reading the card
      clearTimeout(this.errorTimer);
      this.errorTimer = undefined;
      this.stopAllAudio();
      this.scanErrorReasons = reasons;
    },
    retryMobileScan() {
      // the tid is spent and mobileScanResult is gone, so restart from the top via Discovery's
      // deep-link entry. The QR token has to come along: Discovery starts no mobile scan without
      // one, and this one is still good because no body came out of the scan.
      this.$events.goto('Discovery', {
        params: { storeId: this.storeId, qrToken: ScanProcess.qrToken },
      });
    },
    async waitFinish() {
      if (ScanProcess.mobileScanResult) {
        try {
          await ScanProcess.finishMobileScan();
        } catch (e) {
          // only /scanned attaches an errno -- see modules/scan-quality-error for why the code
          // range must not be read outside that endpoint
          const reasons = getScanErrorReason(e && e.errno);
          if (reasons) {
            this.showScanError(reasons, e.errno);
            return;
          }
          const errno = (e && e.message) || '';
          const errorMessage = `${this.$t('welcome.unexpected_error')}${
            errno ? `<br>(${errno})` : ''
          }`;
          throw Error(errorMessage);
        }
      }
      const { url } = await ScanProcess.waitFinish();
      if (!url) {
        console.log('Failed to get 3D model');
        throw Error('Failed to get 3D model');
      }

      if (this.isStamp && !this.second) {
        await this.reInitialize();
      } else {
        this.$store.commit('user/SET_OBJ_URL', url);
        this.url = url;
      }
    },
    async reInitialize() {
      try {
        await ScanProcess.release();
        await wait(3);
        await ScanProcess.reInitialize();
        this.reInitialized = true;
      } catch (e) {
        console.log('Failed to reInitialize', e.message);
        throw Error(`Failed to rescan: ${e.message}`);
      }
    },
    next() {
      if (this.isStamp) {
        if (this.second) {
          this.$events.goto('StampShowResult');
        } else {
          this.$events.goto('StampScanBody', { params: { second: true } });
        }
      } else {
        this.$events.goto('ShowResult');
      }
    },
  },
  computed: {
    ...mapState('user', {
      objUrl: state => state.objUrl,
    }),
    ...mapGetters('mtmUser', ['companyId']),
    ...mapGetters('app', ['storeId']),
    hasScanError() {
      // an array, so the emptiness has to be checked -- `[]` on its own is truthy
      return this.scanErrorReasons.length > 0;
    },
    info() {
      return this.isStamp ? i18n.t('stamp.analyzing') : i18n.t('analyzing.check_err');
    },
    canProceed() {
      if (!this.isStamp) return !!this.url;

      if (this.playingAnalyzing) return false;

      return !!((this.second && this.url) || (!this.second && this.reInitialized));
    },
  },
  watch: {
    canProceed(nv) {
      if (nv) {
        this.next();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.screen-info {
  font-size: 32px;
  line-height: 1.56;
  color: #ffffff;
  z-index: 1;
}
.error {
  color: red;
  z-index: 1;
}
</style>
